/**
 * HostingPortalEventBus — an in-process EventTarget used as the fake SSE in mock mode.
 * Mirrors the wire shape we'll consume from a real AppSync Events / EventSource
 * subscription later (the live runtime swaps this for a real channel).
 *
 * Hooks subscribe with `useStream(...)` and receive every event with a matching
 * type. Capability `mock` executors emit via `ctx.emit(event, payload)`.
 */
export class HostingPortalEventBus extends EventTarget {
  /** Convenience: typed dispatch. */
  emit<T = unknown>(eventName: string, detail: T): void {
    this.dispatchEvent(new CustomEvent(eventName, { detail }))
  }

  /** Convenience: subscribe; returns an unsubscribe fn. */
  on<T = unknown>(eventName: string, handler: (detail: T) => void): () => void {
    const listener = (e: Event) => handler((e as CustomEvent<T>).detail)
    this.addEventListener(eventName, listener)
    return () => this.removeEventListener(eventName, listener)
  }
}

let bus: HostingPortalEventBus | null = null

/** Process-wide singleton event bus (mock mode). */
export function getHostingPortalEventBus(): HostingPortalEventBus {
  if (!bus) bus = new HostingPortalEventBus()
  return bus
}
